import { useState, useEffect } from 'react';
import { useEscapeKey } from '../hooks/useEscapeKey';
import {
  Truck,
  Settings,
  CheckCircle,
  XCircle,
  Loader2,
  Eye,
  EyeOff,
  TestTube,
  Save,
  MapPin,
  User,
  Phone,
  Mail,
  Building,
  Map,
  Plus,
  Trash2,
  Store,
  Tag,
  Info,
  X,
  Package,
  Scale,
  Shield,
} from 'lucide-react';
import { courierApi, storeApi, type Store as StoreType } from '../lib/api';
import { EmptyState } from '../components/EmptyState';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';

type ParcelSize = 'small' | 'medium' | 'large';
type ServiceType = 'inpost_locker_standard' | 'inpost_courier_standard' | 'inpost_courier_express';
type SendingMethod = 'parcel_locker' | 'dispatch_order' | 'pop';
type LabelFormat = 'pdf' | 'zpl' | 'epl';
type LabelSize = 'normal' | 'A6' | 'A4';
type LabelLayout = 'single' | '4_per_page';

interface Sender {
  id: string;
  name: string;
  companyName: string;
  phone: string;
  email: string;
  street: string;
  buildingNumber: string;
  city: string;
  postCode: string;
  storeId: string | null;
  isDefault: boolean;
}

type ParcelTemplate = 'A' | 'B' | 'C' | 'custom';
type LockerGabaryt = 'A' | 'B' | 'C';
type CourierDimensionPreset = 'small' | 'medium' | 'large' | 'custom';
type WeightSource = 'woocommerce' | 'fixed';
type InsuranceType = 'fixed' | 'order_value' | 'cod_value';

interface InPostConfig {
  configured: boolean;
  isActive: boolean;
  organizationId: string;
  sandbox: boolean;
  defaultParcelSize: ParcelSize;
  defaultService: ServiceType;
  sendingMethod: SendingMethod;
  dropoffPoint: string;
  labelFormat: LabelFormat;
  labelSize: LabelSize;
  labelDescription: string;
  senders: Sender[];
  // Legacy parcel settings (kept for backward compatibility)
  parcelTemplate: ParcelTemplate;
  customParcelDimensions: { length: number; width: number; height: number };
  // Weight settings
  weightSource: WeightSource;
  fixedWeight: number;
  // Insurance settings
  insuranceEnabled: boolean;
  insuranceType: InsuranceType;
  fixedInsuranceAmount: number;
  // NEW: Separated Paczkomat and Courier settings
  lockerGabaryt: LockerGabaryt;
  courierDimensionPreset: CourierDimensionPreset;
  courierCustomDimensions: { length: number; width: number; height: number };
  courierWeightSource: WeightSource;
  courierFixedWeight: number;
  // Label filename settings
  dynamicFilename: boolean;
  filenameIncludeStoreName: boolean;
  filenameIncludeStatusLevel1: boolean;
  filenameIncludeStatusLevel2: boolean;
  filenameIncludeStatusLevel3: boolean;
  // Label sorting
  sortLabelsByStatusOrder: boolean;
  // Label layout for bulk download
  labelLayout: LabelLayout;
}

// Shortcodes for label description
const SHORTCODES = [
  { code: '{order_id}', description: 'Numer zamówienia', example: '#12345' },
  { code: '{order_date}', description: 'Data zamówienia', example: '2024-01-15' },
  { code: '{customer_name}', description: 'Imię i nazwisko klienta', example: 'Jan Kowalski' },
  { code: '{products}', description: 'Lista produktów', example: 'Koszulka XL, Spodnie M' },
  { code: '{products_sku}', description: 'SKU produktów', example: 'SKU001, SKU002' },
  { code: '{products_sku_qty}', description: 'SKU produktów z ilościami', example: 'SKU001 x2, SKU002 x1' },
  { code: '{products_count}', description: 'Liczba produktów', example: '3' },
  { code: '{total}', description: 'Wartość zamówienia', example: '199.99 PLN' },
  { code: '{payment_method}', description: 'Metoda płatności', example: 'Przelew' },
  { code: '{store_name}', description: 'Nazwa sklepu', example: 'moj-sklep.pl' },
];

export function CouriersPage() {
  const [loading, setLoading] = useState(true);
  const [stores, setStores] = useState<StoreType[]>([]);
  const [showInPostModal, setShowInPostModal] = useState(false);
  const [inpostConfig, setInpostConfig] = useState<InPostConfig>({
    configured: false,
    isActive: false,
    organizationId: '',
    sandbox: true,
    defaultParcelSize: 'medium',
    defaultService: 'inpost_locker_standard',
    sendingMethod: 'parcel_locker',
    dropoffPoint: '',
    labelFormat: 'pdf',
    labelSize: 'normal',
    labelDescription: '{order_id}',
    senders: [],
    // Legacy parcel settings
    parcelTemplate: 'A',
    customParcelDimensions: { length: 0, width: 0, height: 0 },
    // Weight settings
    weightSource: 'woocommerce',
    fixedWeight: 0.5,
    // Insurance settings
    insuranceEnabled: false,
    insuranceType: 'order_value',
    fixedInsuranceAmount: 0,
    // NEW: Separated Paczkomat and Courier settings
    lockerGabaryt: 'A',
    courierDimensionPreset: 'small',
    courierCustomDimensions: { length: 380, width: 640, height: 80 },
    courierWeightSource: 'woocommerce',
    courierFixedWeight: 0.5,
    dynamicFilename: false,
    filenameIncludeStoreName: true,
    filenameIncludeStatusLevel1: true,
    filenameIncludeStatusLevel2: true,
    filenameIncludeStatusLevel3: true,
    sortLabelsByStatusOrder: false,
    labelLayout: 'single',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [configRes, storesRes] = await Promise.all([
        courierApi.getInPost(),
        storeApi.getAll(),
      ]);

      setStores(storesRes.data);

      const config: InPostConfig = {
        configured: configRes.data.configured,
        isActive: configRes.data.isActive,
        organizationId: '',
        sandbox: true,
        defaultParcelSize: 'medium',
        defaultService: 'inpost_locker_standard',
        sendingMethod: 'parcel_locker',
        dropoffPoint: '',
        labelFormat: 'pdf',
        labelSize: 'normal',
        labelDescription: '{order_id}',
        senders: [],
        // Legacy parcel settings
        parcelTemplate: 'A',
        customParcelDimensions: { length: 0, width: 0, height: 0 },
        // Weight settings
        weightSource: 'woocommerce',
        fixedWeight: 0.5,
        // Insurance settings
        insuranceEnabled: false,
        insuranceType: 'order_value',
        fixedInsuranceAmount: 0,
        // NEW: Separated Paczkomat and Courier settings
        lockerGabaryt: 'A',
        courierDimensionPreset: 'small',
        courierCustomDimensions: { length: 380, width: 640, height: 80 },
        courierWeightSource: 'woocommerce',
        courierFixedWeight: 0.5,
        dynamicFilename: false,
        filenameIncludeStoreName: true,
        filenameIncludeStatusLevel1: true,
        filenameIncludeStatusLevel2: true,
        filenameIncludeStatusLevel3: true,
        sortLabelsByStatusOrder: false,
        labelLayout: 'single',
      };

      if (configRes.data.settings) {
        const s = configRes.data.settings;
        const extSettings = s as unknown as Record<string, unknown>;
        config.organizationId = s.organizationId || '';
        config.sandbox = s.sandbox ?? true;
        config.defaultParcelSize = s.defaultParcelSize || 'medium';
        config.defaultService = s.defaultService || 'inpost_locker_standard';
        config.sendingMethod = extSettings.sendingMethod as SendingMethod || 'parcel_locker';
        config.dropoffPoint = extSettings.dropoffPoint as string || '';
        config.labelFormat = extSettings.labelFormat as LabelFormat || 'pdf';
        config.labelSize = extSettings.labelSize as LabelSize || 'normal';
        config.labelDescription = extSettings.labelDescription as string || '{order_id}';
        config.senders = extSettings.senders as Sender[] || [];
        // Parcel settings
        config.parcelTemplate = extSettings.parcelTemplate as ParcelTemplate || 'A';
        config.customParcelDimensions = extSettings.customParcelDimensions as { length: number; width: number; height: number } || { length: 0, width: 0, height: 0 };
        // Weight settings
        config.weightSource = extSettings.weightSource as WeightSource || 'woocommerce';
        config.fixedWeight = extSettings.fixedWeight as number || 0.5;
        // Insurance settings
        config.insuranceEnabled = extSettings.insuranceEnabled as boolean ?? false;
        config.insuranceType = extSettings.insuranceType as InsuranceType || 'order_value';
        config.fixedInsuranceAmount = extSettings.fixedInsuranceAmount as number || 0;
        // NEW: Separated Paczkomat and Courier settings
        config.lockerGabaryt = extSettings.lockerGabaryt as LockerGabaryt || config.parcelTemplate as LockerGabaryt || 'A';
        config.courierDimensionPreset = extSettings.courierDimensionPreset as CourierDimensionPreset || 'small';
        config.courierCustomDimensions = extSettings.courierCustomDimensions as { length: number; width: number; height: number } || { length: 380, width: 640, height: 80 };
        config.courierWeightSource = extSettings.courierWeightSource as WeightSource || config.weightSource || 'woocommerce';
        config.courierFixedWeight = extSettings.courierFixedWeight as number ?? config.fixedWeight ?? 0.5;
        config.dynamicFilename = extSettings.dynamicFilename as boolean ?? false;
        config.filenameIncludeStoreName = extSettings.filenameIncludeStoreName as boolean ?? true;
        config.filenameIncludeStatusLevel1 = extSettings.filenameIncludeStatusLevel1 as boolean ?? true;
        config.filenameIncludeStatusLevel2 = extSettings.filenameIncludeStatusLevel2 as boolean ?? true;
        config.filenameIncludeStatusLevel3 = extSettings.filenameIncludeStatusLevel3 as boolean ?? true;
        config.sortLabelsByStatusOrder = extSettings.sortLabelsByStatusOrder as boolean ?? false;
        config.labelLayout = extSettings.labelLayout as LabelLayout ?? 'single';
      }

      setInpostConfig(config);
    } catch (error) {
      console.error('Failed to load courier config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleInPost = async () => {
    try {
      const { data } = await courierApi.toggleInPost();
      setInpostConfig(prev => ({ ...prev, isActive: data.isActive }));
    } catch (error) {
      console.error('Failed to toggle InPost:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="loader" />
      </div>
    );
  }

  return (
    <>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between animate-fade-in">
          <div>
            <h1 className="text-3xl font-bold text-text-primary">Integracje kurierskie</h1>
            <p className="text-text-secondary mt-2">Zarządzaj integracjami z firmami kurierskimi</p>
          </div>
        </div>

        {/* Integrations Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* InPost Card */}
          <div className="card animate-slide-up group">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-xl ${
                  inpostConfig.configured && inpostConfig.isActive
                    ? 'bg-gradient-to-br from-accent-primary to-accent-secondary'
                    : 'bg-surface-hover'
                }`}>
                  <Truck className={`w-6 h-6 ${
                    inpostConfig.configured && inpostConfig.isActive ? 'text-dark-900' : 'text-text-muted'
                  }`} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-text-primary">InPost ShipX</h3>
                  <div className="flex items-center gap-2 mt-1">
                    {inpostConfig.configured ? (
                      inpostConfig.isActive ? (
                        <span className="badge badge-success">Aktywna</span>
                      ) : (
                        <span className="badge badge-warning">Wyłączona</span>
                      )
                    ) : (
                      <span className="badge">Nie skonfigurowana</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowInPostModal(true)}
                  className="p-2 rounded-lg bg-surface-hover/50 text-text-secondary hover:text-accent-primary hover:bg-surface-hover transition-all"
                  title="Ustawienia"
                >
                  <Settings className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 rounded-lg bg-surface-hover/30">
                <span className="text-sm text-text-secondary">Typ integracji</span>
                <span className="text-sm text-text-primary">Paczkomaty i Kurier</span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-surface-hover/30">
                <span className="text-sm text-text-secondary">ID Organizacji</span>
                <span className="text-sm text-text-primary">
                  {inpostConfig.organizationId || '—'}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-surface-hover/30">
                <span className="text-sm text-text-secondary">Środowisko</span>
                <span className={`text-sm ${inpostConfig.sandbox ? 'text-warning' : 'text-success'}`}>
                  {inpostConfig.sandbox ? 'Sandbox (testowe)' : 'Produkcja'}
                </span>
              </div>
            </div>

            {inpostConfig.configured && (
              <div className="mt-4 pt-4 border-t border-border-subtle flex items-center justify-between">
                <label className="flex items-center gap-3 cursor-pointer">
                  <div
                    onClick={handleToggleInPost}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      inpostConfig.isActive ? 'bg-success' : 'bg-surface-hover'
                    }`}
                  >
                    <div
                      className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                        inpostConfig.isActive ? 'left-7' : 'left-1'
                      }`}
                    />
                  </div>
                  <span className="text-sm text-text-secondary">
                    {inpostConfig.isActive ? 'Integracja aktywna' : 'Integracja nieaktywna'}
                  </span>
                </label>
              </div>
            )}

            {!inpostConfig.configured && (
              <div className="mt-4 pt-4 border-t border-border-subtle">
                <button
                  onClick={() => setShowInPostModal(true)}
                  className="btn-primary w-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Skonfiguruj integrację
                </button>
              </div>
            )}
          </div>

          {/* Placeholder for future integrations */}
          <div className="card animate-slide-up border-dashed border-2 border-border bg-transparent flex items-center justify-center min-h-[280px]" style={{ animationDelay: '0.1s' }}>
            <div className="text-center">
              <div className="w-12 h-12 rounded-xl bg-surface-hover/50 flex items-center justify-center mx-auto mb-3">
                <Plus className="w-6 h-6 text-text-muted" />
              </div>
              <p className="text-text-muted text-sm">Więcej integracji wkrótce</p>
              <p className="text-text-disabled text-xs mt-1">DPD, DHL, FedEx...</p>
            </div>
          </div>
        </div>
      </div>

      {/* InPost Settings Modal */}
      {showInPostModal && (
        <InPostSettingsModal
          config={inpostConfig}
          stores={stores}
          onClose={() => setShowInPostModal(false)}
          onSave={(updatedConfig) => {
            setInpostConfig(updatedConfig);
            setShowInPostModal(false);
            loadData();
          }}
        />
      )}
    </>
  );
}

// Key icon component
function Key({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
    </svg>
  );
}

// InPost Settings Modal
function InPostSettingsModal({
  config,
  stores,
  onClose,
  onSave,
}: {
  config: InPostConfig;
  stores: StoreType[];
  onClose: () => void;
  onSave: (config: InPostConfig) => void;
}) {
  const confirmDialog = useConfirmDialog();
  const [activeTab, setActiveTab] = useState<'api' | 'sending' | 'labels' | 'shipment' | 'senders'>('api');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [saveResult, setSaveResult] = useState<{ success: boolean; message: string } | null>(null);
  const [showAddSenderModal, setShowAddSenderModal] = useState(false);
  const [editingSender, setEditingSender] = useState<Sender | null>(null);

  // Close modal on ESC (disabled when sender sub-modal is open)
  useEscapeKey(onClose, !showAddSenderModal && !editingSender);

  // Form state
  const [token, setToken] = useState('');
  const [organizationId, setOrganizationId] = useState(config.organizationId);
  const [sandbox, setSandbox] = useState(config.sandbox);
  // Default service is auto-detected from order, keep for backward compatibility
  const defaultService: ServiceType = config.defaultService;
  const [sendingMethod, setSendingMethod] = useState<SendingMethod>(config.sendingMethod);
  const [dropoffPoint, setDropoffPoint] = useState(config.dropoffPoint);
  const [labelFormat, setLabelFormat] = useState<LabelFormat>(config.labelFormat);
  const [labelSize, setLabelSize] = useState<LabelSize>(config.labelSize);
  const [labelDescription, setLabelDescription] = useState(config.labelDescription);
  const [senders, setSenders] = useState<Sender[]>(config.senders);
  // Insurance settings
  const [insuranceEnabled, setInsuranceEnabled] = useState(config.insuranceEnabled);
  const [insuranceType, setInsuranceType] = useState<InsuranceType>(config.insuranceType);
  const [fixedInsuranceAmount, setFixedInsuranceAmount] = useState(config.fixedInsuranceAmount);
  // NEW: Separated Paczkomat settings
  const [lockerGabaryt, setLockerGabaryt] = useState<LockerGabaryt>(config.lockerGabaryt);
  // NEW: Separated Courier settings
  const [courierDimensionPreset, setCourierDimensionPreset] = useState<CourierDimensionPreset>(config.courierDimensionPreset);
  const [courierCustomDimensions, setCourierCustomDimensions] = useState(config.courierCustomDimensions);
  const [courierWeightSource, setCourierWeightSource] = useState<WeightSource>(config.courierWeightSource);
  const [courierFixedWeight, setCourierFixedWeight] = useState(config.courierFixedWeight);
  // Dynamic filename for bulk download
  const [dynamicFilename, setDynamicFilename] = useState(config.dynamicFilename);
  const [filenameIncludeStoreName, setFilenameIncludeStoreName] = useState(config.filenameIncludeStoreName);
  const [filenameIncludeStatusLevel1, setFilenameIncludeStatusLevel1] = useState(config.filenameIncludeStatusLevel1);
  const [filenameIncludeStatusLevel2, setFilenameIncludeStatusLevel2] = useState(config.filenameIncludeStatusLevel2);
  const [filenameIncludeStatusLevel3, setFilenameIncludeStatusLevel3] = useState(config.filenameIncludeStatusLevel3);
  // Sort labels by status order
  const [sortLabelsByStatusOrder, setSortLabelsByStatusOrder] = useState(config.sortLabelsByStatusOrder);
  // Label layout for bulk download
  const [labelLayout, setLabelLayout] = useState<LabelLayout>(config.labelLayout);

  // Sync local state with config prop changes (important for when data loads after initial render)
  useEffect(() => {
    setOrganizationId(config.organizationId);
    setSandbox(config.sandbox);
    setSendingMethod(config.sendingMethod);
    setDropoffPoint(config.dropoffPoint);
    setLabelFormat(config.labelFormat);
    setLabelSize(config.labelSize);
    setLabelDescription(config.labelDescription);
    setSenders(config.senders);
    setInsuranceEnabled(config.insuranceEnabled);
    setInsuranceType(config.insuranceType);
    setFixedInsuranceAmount(config.fixedInsuranceAmount);
    setLockerGabaryt(config.lockerGabaryt);
    setCourierDimensionPreset(config.courierDimensionPreset);
    setCourierCustomDimensions(config.courierCustomDimensions);
    setCourierWeightSource(config.courierWeightSource);
    setCourierFixedWeight(config.courierFixedWeight);
    setDynamicFilename(config.dynamicFilename);
    setFilenameIncludeStoreName(config.filenameIncludeStoreName);
    setFilenameIncludeStatusLevel1(config.filenameIncludeStatusLevel1);
    setFilenameIncludeStatusLevel2(config.filenameIncludeStatusLevel2);
    setFilenameIncludeStatusLevel3(config.filenameIncludeStatusLevel3);
    setSortLabelsByStatusOrder(config.sortLabelsByStatusOrder);
    setLabelLayout(config.labelLayout);
  }, [config]);

  // Legacy computed values - derived from new settings for backward compatibility
  const parcelTemplate: ParcelTemplate = lockerGabaryt;
  const customParcelDimensions = courierCustomDimensions;
  const weightSource = courierWeightSource;
  const fixedWeight = courierFixedWeight;

  const handleTest = async () => {
    if (!token || !organizationId) {
      setTestResult({ success: false, message: 'Podaj ID organizacji i token API' });
      return;
    }

    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await courierApi.testInPost({ token, organizationId, sandbox });
      if (data.success) {
        setTestResult({ success: true, message: data.message || `Połączono: ${data.organizationName}` });
      } else {
        setTestResult({ success: false, message: data.error || 'Nie udało się połączyć' });
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string; success?: boolean } } };
      const errorMessage = error.response?.data?.error || 'Błąd podczas testowania połączenia';
      setTestResult({ success: false, message: errorMessage });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!token && !config.configured) {
      setSaveResult({ success: false, message: 'Token jest wymagany' });
      return;
    }
    if (!organizationId) {
      setSaveResult({ success: false, message: 'ID organizacji jest wymagane' });
      return;
    }

    setSaving(true);
    setSaveResult(null);

    // Map parcel template to InPost parcel size for backward compatibility
    const templateToSizeMap: Record<ParcelTemplate, ParcelSize> = {
      'A': 'small',
      'B': 'medium',
      'C': 'large',
      'custom': 'medium',
    };
    const computedParcelSize = templateToSizeMap[parcelTemplate];

    try {
      const { data } = await courierApi.saveInPost({
        token: token || 'UNCHANGED',
        organizationId,
        sandbox,
        defaultParcelSize: computedParcelSize,
        defaultService,
        sendingMethod,
        dropoffPoint,
        labelFormat,
        labelSize,
        labelDescription,
        senders,
        // Legacy parcel settings (for backward compatibility)
        parcelTemplate,
        customParcelDimensions,
        // Legacy weight settings
        weightSource,
        fixedWeight,
        // Insurance settings
        insuranceEnabled,
        insuranceType,
        fixedInsuranceAmount,
        // NEW: Separated Paczkomat and Courier settings
        lockerGabaryt,
        courierDimensionPreset,
        courierCustomDimensions,
        courierWeightSource,
        courierFixedWeight,
        dynamicFilename,
        filenameIncludeStoreName,
        filenameIncludeStatusLevel1,
        filenameIncludeStatusLevel2,
        filenameIncludeStatusLevel3,
        sortLabelsByStatusOrder,
        labelLayout,
      } as never);
      setSaveResult({ success: true, message: data.message });

      // Update config
      onSave({
        ...config,
        configured: true,
        isActive: true,
        organizationId,
        sandbox,
        defaultParcelSize: computedParcelSize,
        defaultService,
        sendingMethod,
        dropoffPoint,
        labelFormat,
        labelSize,
        labelDescription,
        senders,
        parcelTemplate,
        customParcelDimensions,
        weightSource,
        fixedWeight,
        insuranceEnabled,
        insuranceType,
        fixedInsuranceAmount,
        // NEW: Separated Paczkomat and Courier settings
        lockerGabaryt,
        courierDimensionPreset,
        courierCustomDimensions,
        courierWeightSource,
        courierFixedWeight,
        dynamicFilename,
        filenameIncludeStoreName,
        filenameIncludeStatusLevel1,
        filenameIncludeStatusLevel2,
        filenameIncludeStatusLevel3,
        sortLabelsByStatusOrder,
        labelLayout,
      });
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      const errorMessage = error.response?.data?.error || 'Błąd podczas zapisywania konfiguracji';
      setSaveResult({ success: false, message: errorMessage });
    } finally {
      setSaving(false);
    }
  };

  const handleAddSender = (sender: Omit<Sender, 'id'>) => {
    const newSender: Sender = {
      ...sender,
      id: Date.now().toString(),
    };
    setSenders([...senders, newSender]);
    setShowAddSenderModal(false);
  };

  const handleEditSender = (sender: Sender) => {
    setSenders(senders.map(s => s.id === sender.id ? sender : s));
    setEditingSender(null);
  };

  const handleDeleteSender = async (id: string) => {
    const ok = await confirmDialog({
      title: 'Usuń nadawcę',
      message: 'Czy na pewno chcesz usunąć tego nadawcę?',
      confirmLabel: 'Usuń nadawcę',
    });
    if (!ok) return;
    setSenders(senders.filter(s => s.id !== id));
  };

  const handleSetDefaultSender = (id: string) => {
    setSenders(senders.map(s => ({
      ...s,
      isDefault: s.id === id,
    })));
  };

  const insertShortcode = (code: string) => {
    setLabelDescription(prev => prev + code);
  };

  const tabs = [
    { id: 'api', name: 'Połączenie API' },
    { id: 'sending', name: 'Nadawanie' },
    { id: 'labels', name: 'Etykiety' },
    { id: 'shipment', name: 'Przesyłki' },
    { id: 'senders', name: 'Nadawcy' },
  ] as const;

  return (
    <div
      className="fixed inset-0 bg-surface-base/95 backdrop-blur-md flex items-center justify-center p-4 animate-fade-in z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-surface-raised border border-border rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border-subtle">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-accent-primary to-accent-secondary">
              <Truck className="w-6 h-6 text-dark-900" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-text-primary">InPost ShipX</h2>
              <p className="text-sm text-text-secondary">Konfiguracja integracji</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-6 pt-4">
          <div className="flex gap-1 p-1 bg-surface-hover/50 rounded-lg overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-accent-primary text-dark-900'
                    : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                {tab.name}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* API Tab */}
          {activeTab === 'api' && (
            <div className="space-y-5">
              {/* Organization ID - FIRST */}
              <div>
                <label className="label">
                  <Building className="w-4 h-4 inline mr-2" />
                  ID Organizacji *
                </label>
                <input
                  type="text"
                  value={organizationId}
                  onChange={(e) => setOrganizationId(e.target.value)}
                  placeholder="np. 12345"
                  className="input"
                />
              </div>

              {/* Token - SECOND */}
              <div>
                <label className="label">
                  <Key className="w-4 h-4 inline mr-2" />
                  Token API {!config.configured && '*'}
                </label>
                {config.configured && !token && (
                  <div className="flex items-center gap-2 mb-2 px-3 py-2 bg-success/10 border border-success/30 rounded-lg">
                    <CheckCircle className="w-4 h-4 text-success flex-shrink-0" />
                    <span className="text-sm text-success">Token zapisany</span>
                    <span className="text-xs text-text-secondary">(zostaw puste, aby zachować obecny)</span>
                  </div>
                )}
                <div className="relative">
                  <input
                    type={showToken ? 'text' : 'password'}
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    placeholder={config.configured ? 'Wpisz nowy token, aby go zmienić' : 'Wklej token API z panelu InPost'}
                    className="input pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowToken(!showToken)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary"
                  >
                    {showToken ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-xs text-text-muted mt-2 p-3 bg-surface-hover/30 rounded-lg">
                  Znajdziesz go w panelu InPost Manager → Ustawienia → API
                </p>
              </div>

              {/* Sandbox toggle */}
              <div className="flex items-center justify-between p-4 bg-surface-hover/30 rounded-xl">
                <div>
                  <p className="text-sm font-medium text-text-primary">Tryb testowy (Sandbox)</p>
                  <p className="text-xs text-text-muted">
                    {sandbox ? 'Środowisko testowe - przełącz dla produkcji' : 'Środowisko produkcyjne'}
                  </p>
                </div>
                <div
                  onClick={() => setSandbox(!sandbox)}
                  className={`relative w-12 h-6 rounded-full cursor-pointer transition-colors ${
                    sandbox ? 'bg-yellow-500' : 'bg-success'
                  }`}
                >
                  <div
                    className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                      sandbox ? 'left-7' : 'left-1'
                    }`}
                  />
                </div>
              </div>

              {/* Test connection */}
              <div className="flex items-center gap-4">
                <button
                  onClick={handleTest}
                  disabled={testing || !token || !organizationId}
                  className="btn-secondary"
                >
                  {testing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <TestTube className="w-4 h-4 mr-2" />
                  )}
                  Testuj połączenie
                </button>
                {testResult && (
                  <div className={`flex items-center gap-2 text-sm ${
                    testResult.success ? 'text-success' : 'text-danger'
                  }`}>
                    {testResult.success ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      <XCircle className="w-4 h-4" />
                    )}
                    {testResult.message}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Sending Tab */}
          {activeTab === 'sending' && (
            <div className="space-y-4">
              <h4 className="text-text-primary font-medium mb-4">Sposób nadawania paczek</h4>

              {/* Parcel Locker Option */}
              <div>
                <label
                  className={`flex items-start p-4 rounded-xl cursor-pointer transition-all ${
                    sendingMethod === 'parcel_locker'
                      ? 'bg-accent-primary/10 border border-accent-primary/50'
                      : 'bg-surface-hover/30 border border-transparent hover:bg-surface-hover/50'
                  }`}
                >
                  <input
                    type="radio"
                    name="sendingMethod"
                    value="parcel_locker"
                    checked={sendingMethod === 'parcel_locker'}
                    onChange={(e) => setSendingMethod(e.target.value as SendingMethod)}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded-full border-2 mr-4 mt-0.5 flex items-center justify-center ${
                    sendingMethod === 'parcel_locker'
                      ? 'border-accent-primary bg-accent-primary'
                      : 'border-gray-500'
                  }`}>
                    {sendingMethod === 'parcel_locker' && (
                      <div className="w-2 h-2 rounded-full bg-surface-base" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-text-primary">Nadanie w paczkomacie</p>
                    <p className="text-xs text-text-muted mt-0.5">Nadaj paczkę w dowolnym paczkomacie InPost</p>
                  </div>
                </label>

                {/* Paczkomat field - RIGHT BELOW the option when selected */}
                {sendingMethod === 'parcel_locker' && (
                  <div className="mt-3 ml-9 p-4 bg-surface-hover/20 rounded-xl">
                    <label className="label">
                      <MapPin className="w-4 h-4 inline mr-2" />
                      Domyślny paczkomat nadawczy
                    </label>
                    <input
                      type="text"
                      value={dropoffPoint}
                      onChange={(e) => setDropoffPoint(e.target.value.toUpperCase())}
                      placeholder="np. WAW123M"
                      className="input"
                    />
                    <p className="text-xs text-text-muted mt-1">
                      Kod paczkomatu, do którego będziesz dostarczać paczki do nadania
                    </p>
                  </div>
                )}
              </div>

              {/* POP Option */}
              <label
                className={`flex items-start p-4 rounded-xl cursor-pointer transition-all ${
                  sendingMethod === 'pop'
                    ? 'bg-accent-primary/10 border border-accent-primary/50'
                    : 'bg-surface-hover/30 border border-transparent hover:bg-surface-hover/50'
                }`}
              >
                <input
                  type="radio"
                  name="sendingMethod"
                  value="pop"
                  checked={sendingMethod === 'pop'}
                  onChange={(e) => setSendingMethod(e.target.value as SendingMethod)}
                  className="sr-only"
                />
                <div className={`w-5 h-5 rounded-full border-2 mr-4 mt-0.5 flex items-center justify-center ${
                  sendingMethod === 'pop'
                    ? 'border-accent-primary bg-accent-primary'
                    : 'border-gray-500'
                }`}>
                  {sendingMethod === 'pop' && (
                    <div className="w-2 h-2 rounded-full bg-surface-base" />
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-text-primary">Nadanie w punkcie nadawczym</p>
                  <p className="text-xs text-text-muted mt-0.5">Nadaj paczkę w PaczkoPunkcie lub POK</p>
                </div>
              </label>

              {/* Dispatch Order Option */}
              <label
                className={`flex items-start p-4 rounded-xl cursor-pointer transition-all ${
                  sendingMethod === 'dispatch_order'
                    ? 'bg-accent-primary/10 border border-accent-primary/50'
                    : 'bg-surface-hover/30 border border-transparent hover:bg-surface-hover/50'
                }`}
              >
                <input
                  type="radio"
                  name="sendingMethod"
                  value="dispatch_order"
                  checked={sendingMethod === 'dispatch_order'}
                  onChange={(e) => setSendingMethod(e.target.value as SendingMethod)}
                  className="sr-only"
                />
                <div className={`w-5 h-5 rounded-full border-2 mr-4 mt-0.5 flex items-center justify-center ${
                  sendingMethod === 'dispatch_order'
                    ? 'border-accent-primary bg-accent-primary'
                    : 'border-gray-500'
                }`}>
                  {sendingMethod === 'dispatch_order' && (
                    <div className="w-2 h-2 rounded-full bg-surface-base" />
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-text-primary">Odbiór przez kuriera</p>
                  <p className="text-xs text-text-muted mt-0.5">Kurier odbierze paczkę z podanego adresu</p>
                </div>
              </label>
            </div>
          )}

          {/* Labels Tab */}
          {activeTab === 'labels' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Label Format */}
                <div>
                  <label className="label">Format etykiety</label>
                  <div className="space-y-2">
                    {[
                      { value: 'pdf', label: 'PDF', desc: 'Uniwersalny format' },
                      { value: 'zpl', label: 'ZPL', desc: 'Drukarki Zebra' },
                      { value: 'epl', label: 'EPL', desc: 'Drukarki termiczne' },
                    ].map((option) => (
                      <label
                        key={option.value}
                        className={`flex items-center p-3 rounded-lg cursor-pointer transition-all ${
                          labelFormat === option.value
                            ? 'bg-accent-primary/10 border border-accent-primary/50'
                            : 'bg-surface-hover/30 border border-transparent hover:bg-surface-hover/50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="labelFormat"
                          value={option.value}
                          checked={labelFormat === option.value}
                          onChange={(e) => setLabelFormat(e.target.value as LabelFormat)}
                          className="sr-only"
                        />
                        <div className={`w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center ${
                          labelFormat === option.value
                            ? 'border-accent-primary bg-accent-primary'
                            : 'border-gray-500'
                        }`}>
                          {labelFormat === option.value && (
                            <div className="w-1.5 h-1.5 rounded-full bg-surface-base" />
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-text-primary">{option.label}</p>
                          <p className="text-xs text-text-muted">{option.desc}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Label Size */}
                <div>
                  <label className="label">Rozmiar etykiety</label>
                  <div className="space-y-2">
                    {[
                      { value: 'normal', label: 'Standardowa', desc: 'Dla przesyłek paczkomatowych' },
                      { value: 'A6', label: 'A6 (10.5 × 14.8 cm)', desc: 'Dla przesyłek kurierskich' },
                      { value: 'A4', label: 'A4 (21 × 29.7 cm)', desc: 'Pełna strona' },
                    ].map((option) => (
                      <label
                        key={option.value}
                        className={`flex items-center p-3 rounded-lg cursor-pointer transition-all ${
                          labelSize === option.value
                            ? 'bg-accent-primary/10 border border-accent-primary/50'
                            : 'bg-surface-hover/30 border border-transparent hover:bg-surface-hover/50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="labelSize"
                          value={option.value}
                          checked={labelSize === option.value}
                          onChange={(e) => setLabelSize(e.target.value as LabelSize)}
                          className="sr-only"
                        />
                        <div className={`w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center ${
                          labelSize === option.value
                            ? 'border-accent-primary bg-accent-primary'
                            : 'border-gray-500'
                        }`}>
                          {labelSize === option.value && (
                            <div className="w-1.5 h-1.5 rounded-full bg-surface-base" />
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-text-primary">{option.label}</p>
                          <p className="text-xs text-text-muted">{option.desc}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Label Description with Shortcodes */}
              <div>
                <label className="label">
                  <Tag className="w-4 h-4 inline mr-2" />
                  Dodatkowy opis na etykiecie
                </label>
                <textarea
                  value={labelDescription}
                  onChange={(e) => setLabelDescription(e.target.value)}
                  placeholder="np. {order_id} - {products}"
                  className="input min-h-[80px] resize-y"
                />
                <div className="mt-3">
                  <p className="text-xs text-text-muted mb-2">Kliknij shortcode, aby dodać go do opisu:</p>
                  <div className="flex flex-wrap gap-2">
                    {SHORTCODES.map((sc) => (
                      <button
                        key={sc.code}
                        type="button"
                        onClick={() => insertShortcode(sc.code)}
                        className="inline-flex items-center px-2 py-1 bg-surface-hover/50 hover:bg-surface-hover border border-border rounded-lg text-xs text-text-secondary hover:text-text-primary transition-all"
                        title={`${sc.description} (np. ${sc.example})`}
                      >
                        <code className="text-accent-primary">{sc.code}</code>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="mt-3 p-3 bg-accent-primary/10 border border-accent-primary/30 rounded-xl">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-accent-primary flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-text-secondary">
                      <p className="font-medium text-accent-primary mb-1">Dostępne shortcodes:</p>
                      <ul className="space-y-0.5">
                        {SHORTCODES.map((sc) => (
                          <li key={sc.code}>
                            <code className="text-text-primary">{sc.code}</code> - {sc.description}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Dynamic Filename Option */}
              <div className="p-4 bg-surface-hover/20 rounded-xl border border-border-subtle">
                <label className="flex items-start gap-3 cursor-pointer">
                  <div className="mt-0.5">
                    <input
                      type="checkbox"
                      checked={dynamicFilename}
                      onChange={(e) => setDynamicFilename(e.target.checked)}
                      className="w-4 h-4 rounded border-border bg-surface-hover text-accent-primary focus:ring-accent-primary focus:ring-offset-0"
                    />
                  </div>
                  <div>
                    <p className="text-text-primary font-medium">Dynamiczna nazwa pliku PDF</p>
                    <p className="text-xs text-text-secondary mt-1">
                      Przy pobieraniu wielu etykiet nazwa pliku będzie zawierać wybrane elementy i datę.
                    </p>
                    <p className="text-xs text-text-muted mt-2">
                      Przykład: <code className="text-accent-primary">walamix_w_realizacji_24.01.2026.pdf</code>
                    </p>
                  </div>
                </label>

                {/* Filename components selection - only shown when dynamic filename is enabled */}
                {dynamicFilename && (
                  <div className="mt-4 pl-7 space-y-2 border-l-2 border-border">
                    <p className="text-xs text-text-muted mb-2">Wybierz elementy do uwzględnienia w nazwie:</p>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filenameIncludeStoreName}
                        onChange={(e) => setFilenameIncludeStoreName(e.target.checked)}
                        className="w-4 h-4 rounded border-border bg-surface-hover text-accent-primary focus:ring-accent-primary focus:ring-offset-0"
                      />
                      <span className="text-sm text-text-secondary">Nazwa sklepu</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filenameIncludeStatusLevel1}
                        onChange={(e) => setFilenameIncludeStatusLevel1(e.target.checked)}
                        className="w-4 h-4 rounded border-border bg-surface-hover text-accent-primary focus:ring-accent-primary focus:ring-offset-0"
                      />
                      <span className="text-sm text-text-secondary">Statusy zamówień (poziom 1)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filenameIncludeStatusLevel2}
                        onChange={(e) => setFilenameIncludeStatusLevel2(e.target.checked)}
                        className="w-4 h-4 rounded border-border bg-surface-hover text-accent-primary focus:ring-accent-primary focus:ring-offset-0"
                      />
                      <span className="text-sm text-text-secondary">Statusy zamówień (poziom 2)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filenameIncludeStatusLevel3}
                        onChange={(e) => setFilenameIncludeStatusLevel3(e.target.checked)}
                        className="w-4 h-4 rounded border-border bg-surface-hover text-accent-primary focus:ring-accent-primary focus:ring-offset-0"
                      />
                      <span className="text-sm text-text-secondary">Statusy zamówień (poziom 3)</span>
                    </label>
                  </div>
                )}
              </div>

              {/* Sort Labels by Status Order Option */}
              <div className="p-4 bg-surface-hover/20 rounded-xl border border-border-subtle">
                <label className="flex items-start gap-3 cursor-pointer">
                  <div className="mt-0.5">
                    <input
                      type="checkbox"
                      checked={sortLabelsByStatusOrder}
                      onChange={(e) => setSortLabelsByStatusOrder(e.target.checked)}
                      className="w-4 h-4 rounded border-border bg-surface-hover text-accent-primary focus:ring-accent-primary focus:ring-offset-0"
                    />
                  </div>
                  <div>
                    <p className="text-text-primary font-medium">Sortuj etykiety według kolejności statusów</p>
                    <p className="text-xs text-text-secondary mt-1">
                      Przy łączeniu wielu etykiet w jeden PDF, zamówienia będą sortowane według pozycji statusu w grupie statusów.
                    </p>
                    <p className="text-xs text-text-muted mt-2">
                      Przykład: Jeśli status "Do realizacji" ma podstatusy 1, 2, 3 - etykiety będą w tej kolejności.
                    </p>
                  </div>
                </label>
              </div>

              {/* Label Layout Option */}
              <div className="p-4 bg-surface-hover/20 rounded-xl border border-border-subtle">
                <label className="text-sm text-text-secondary mb-3 block">Układ etykiet przy masowym pobieraniu</label>
                <div className="space-y-3">
                  <label
                    className={`flex items-center p-3 rounded-lg cursor-pointer transition-all ${
                      labelLayout === 'single'
                        ? 'bg-accent-primary/10 border border-accent-primary/50'
                        : 'bg-surface-hover/30 border border-transparent hover:bg-surface-hover/50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="labelLayout"
                      value="single"
                      checked={labelLayout === 'single'}
                      onChange={(e) => setLabelLayout(e.target.value as LabelLayout)}
                      className="sr-only"
                    />
                    <div className={`w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center ${
                      labelLayout === 'single'
                        ? 'border-accent-primary bg-accent-primary'
                        : 'border-gray-500'
                    }`}>
                      {labelLayout === 'single' && (
                        <div className="w-1.5 h-1.5 rounded-full bg-surface-base" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-text-primary">Pojedyncze strony</p>
                      <p className="text-xs text-text-muted">Każda etykieta na osobnej stronie (domyślne)</p>
                    </div>
                  </label>

                  <label
                    className={`flex items-center p-3 rounded-lg cursor-pointer transition-all ${
                      labelLayout === '4_per_page'
                        ? 'bg-accent-primary/10 border border-accent-primary/50'
                        : 'bg-surface-hover/30 border border-transparent hover:bg-surface-hover/50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="labelLayout"
                      value="4_per_page"
                      checked={labelLayout === '4_per_page'}
                      onChange={(e) => setLabelLayout(e.target.value as LabelLayout)}
                      className="sr-only"
                    />
                    <div className={`w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center ${
                      labelLayout === '4_per_page'
                        ? 'border-accent-primary bg-accent-primary'
                        : 'border-gray-500'
                    }`}>
                      {labelLayout === '4_per_page' && (
                        <div className="w-1.5 h-1.5 rounded-full bg-surface-base" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-text-primary">4 etykiety na A4 (2x2)</p>
                      <p className="text-xs text-text-muted">4 etykiety ułożone w siatce 2x2 na kartce A4</p>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Shipment Tab */}
          {activeTab === 'shipment' && (
            <div className="space-y-8">
              {/* Info about auto-detection */}
              <div className="p-3 bg-accent-primary/10 border border-accent-primary/30 rounded-xl">
                <p className="text-xs text-text-secondary">
                  <Info className="w-3 h-3 inline mr-1 text-accent-primary" />
                  Typ wysyłki (Paczkomat/Kurier) jest automatycznie wykrywany na podstawie metody wysyłki wybranej przez klienta w zamówieniu WooCommerce.
                </p>
              </div>

              {/* PACZKOMAT Section */}
              <div className="p-4 bg-surface-hover/20 rounded-xl border border-border-subtle">
                <h4 className="text-text-primary font-medium mb-4 flex items-center">
                  <Package className="w-4 h-4 mr-2 text-accent-primary" />
                  Ustawienia paczkomatu
                </h4>
                <p className="text-xs text-text-muted mb-4">
                  Wybierz domyślny gabaryt dla przesyłek do paczkomatu. Waga nie jest wymagana - InPost używa maksymalnej wagi gabarytu.
                </p>
                <div className="space-y-3">
                  {[
                    { value: 'A', label: 'Gabaryt A (mały)', desc: '8 × 38 × 64 cm, max 10 kg' },
                    { value: 'B', label: 'Gabaryt B (średni)', desc: '19 × 38 × 64 cm, max 25 kg' },
                    { value: 'C', label: 'Gabaryt C (duży)', desc: '41 × 38 × 64 cm, max 25 kg' },
                  ].map((option) => (
                    <label
                      key={option.value}
                      className={`flex items-center p-3 rounded-lg cursor-pointer transition-all ${
                        lockerGabaryt === option.value
                          ? 'bg-accent-primary/10 border border-accent-primary/50'
                          : 'bg-surface-hover/30 border border-transparent hover:bg-surface-hover/50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="lockerGabaryt"
                        value={option.value}
                        checked={lockerGabaryt === option.value}
                        onChange={(e) => setLockerGabaryt(e.target.value as LockerGabaryt)}
                        className="sr-only"
                      />
                      <div className={`w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center ${
                        lockerGabaryt === option.value
                          ? 'border-accent-primary bg-accent-primary'
                          : 'border-gray-500'
                      }`}>
                        {lockerGabaryt === option.value && (
                          <div className="w-1.5 h-1.5 rounded-full bg-surface-base" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-text-primary">{option.label}</p>
                        <p className="text-xs text-text-muted">{option.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* KURIER Section */}
              <div className="p-4 bg-surface-hover/20 rounded-xl border border-border-subtle">
                <h4 className="text-text-primary font-medium mb-4 flex items-center">
                  <Truck className="w-4 h-4 mr-2 text-accent-secondary" />
                  Ustawienia kuriera
                </h4>
                <p className="text-xs text-text-muted mb-4">
                  Dla przesyłek kurierskich musisz określić zarówno wymiary jak i wagę. Waga jest wymagana i będzie widoczna na etykiecie.
                </p>

                {/* Courier Dimension Presets */}
                <div className="mb-6">
                  <label className="text-sm text-text-secondary mb-3 block">Domyślne wymiary paczki</label>
                  <div className="space-y-3">
                    {[
                      { value: 'small', label: 'Mała', desc: '8 × 38 × 64 cm (jak gabaryt A)' },
                      { value: 'medium', label: 'Średnia', desc: '19 × 38 × 64 cm (jak gabaryt B)' },
                      { value: 'large', label: 'Duża', desc: '41 × 38 × 64 cm (jak gabaryt C)' },
                      { value: 'custom', label: 'Niestandardowe', desc: 'Podaj własne wymiary' },
                    ].map((option) => (
                      <label
                        key={option.value}
                        className={`flex items-center p-3 rounded-lg cursor-pointer transition-all ${
                          courierDimensionPreset === option.value
                            ? 'bg-accent-secondary/10 border border-accent-secondary/50'
                            : 'bg-surface-hover/30 border border-transparent hover:bg-surface-hover/50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="courierDimensionPreset"
                          value={option.value}
                          checked={courierDimensionPreset === option.value}
                          onChange={(e) => setCourierDimensionPreset(e.target.value as CourierDimensionPreset)}
                          className="sr-only"
                        />
                        <div className={`w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center ${
                          courierDimensionPreset === option.value
                            ? 'border-accent-secondary bg-accent-secondary'
                            : 'border-gray-500'
                        }`}>
                          {courierDimensionPreset === option.value && (
                            <div className="w-1.5 h-1.5 rounded-full bg-surface-base" />
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-text-primary">{option.label}</p>
                          <p className="text-xs text-text-muted">{option.desc}</p>
                        </div>
                      </label>
                    ))}
                  </div>

                  {/* Custom dimensions fields for courier */}
                  {courierDimensionPreset === 'custom' && (
                    <div className="mt-4 p-4 bg-surface-raised/50 rounded-lg">
                      <p className="text-xs text-text-secondary mb-3">Podaj wymiary paczki (w centymetrach):</p>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="text-xs text-text-muted mb-1 block">Długość (cm)</label>
                          <input
                            type="number"
                            value={courierCustomDimensions.length || ''}
                            onChange={(e) => setCourierCustomDimensions({
                              ...courierCustomDimensions,
                              length: parseFloat(e.target.value) || 0
                            })}
                            placeholder="64"
                            className="input text-sm"
                            min="1"
                            max="120"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-text-muted mb-1 block">Szerokość (cm)</label>
                          <input
                            type="number"
                            value={courierCustomDimensions.width || ''}
                            onChange={(e) => setCourierCustomDimensions({
                              ...courierCustomDimensions,
                              width: parseFloat(e.target.value) || 0
                            })}
                            placeholder="38"
                            className="input text-sm"
                            min="1"
                            max="120"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-text-muted mb-1 block">Wysokość (cm)</label>
                          <input
                            type="number"
                            value={courierCustomDimensions.height || ''}
                            onChange={(e) => setCourierCustomDimensions({
                              ...courierCustomDimensions,
                              height: parseFloat(e.target.value) || 0
                            })}
                            placeholder="19"
                            className="input text-sm"
                            min="1"
                            max="120"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-warning/80 mt-2">
                        <Info className="w-3 h-3 inline mr-1" />
                        Max suma 3 boków: 220 cm, najdłuższy bok: 120 cm
                      </p>
                    </div>
                  )}
                </div>

                {/* Courier Weight Section */}
                <div className="pt-4 border-t border-border-subtle">
                  <label className="text-sm text-text-secondary mb-3 block flex items-center">
                    <Scale className="w-4 h-4 mr-2 text-accent-secondary" />
                    Waga paczki (wymagana)
                  </label>
                  <div className="space-y-3">
                    <label
                      className={`flex items-start p-3 rounded-lg cursor-pointer transition-all ${
                        courierWeightSource === 'woocommerce'
                          ? 'bg-accent-secondary/10 border border-accent-secondary/50'
                          : 'bg-surface-hover/30 border border-transparent hover:bg-surface-hover/50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="courierWeightSource"
                        value="woocommerce"
                        checked={courierWeightSource === 'woocommerce'}
                        onChange={(e) => setCourierWeightSource(e.target.value as WeightSource)}
                        className="sr-only"
                      />
                      <div className={`w-4 h-4 rounded-full border-2 mr-3 mt-0.5 flex items-center justify-center ${
                        courierWeightSource === 'woocommerce'
                          ? 'border-accent-secondary bg-accent-secondary'
                          : 'border-gray-500'
                      }`}>
                        {courierWeightSource === 'woocommerce' && (
                          <div className="w-1.5 h-1.5 rounded-full bg-surface-base" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-text-primary">Pobieraj z WooCommerce</p>
                        <p className="text-xs text-text-muted">Suma wag produktów z zamówienia (zalecane)</p>
                      </div>
                    </label>

                    <label
                      className={`flex items-start p-3 rounded-lg cursor-pointer transition-all ${
                        courierWeightSource === 'fixed'
                          ? 'bg-accent-secondary/10 border border-accent-secondary/50'
                          : 'bg-surface-hover/30 border border-transparent hover:bg-surface-hover/50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="courierWeightSource"
                        value="fixed"
                        checked={courierWeightSource === 'fixed'}
                        onChange={(e) => setCourierWeightSource(e.target.value as WeightSource)}
                        className="sr-only"
                      />
                      <div className={`w-4 h-4 rounded-full border-2 mr-3 mt-0.5 flex items-center justify-center ${
                        courierWeightSource === 'fixed'
                          ? 'border-accent-secondary bg-accent-secondary'
                          : 'border-gray-500'
                      }`}>
                        {courierWeightSource === 'fixed' && (
                          <div className="w-1.5 h-1.5 rounded-full bg-surface-base" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-text-primary">Stała waga</p>
                        <p className="text-xs text-text-muted">Użyj tej samej wagi dla wszystkich przesyłek kurierskich</p>
                      </div>
                    </label>

                    {courierWeightSource === 'fixed' && (
                      <div className="mt-3 ml-7 p-3 bg-surface-raised/50 rounded-lg">
                        <label className="text-xs text-text-secondary mb-1 block">Waga (kg)</label>
                        <input
                          type="number"
                          value={courierFixedWeight || ''}
                          onChange={(e) => setCourierFixedWeight(parseFloat(e.target.value) || 0)}
                          placeholder="0.5"
                          className="input text-sm w-32"
                          min="0.1"
                          max="30"
                          step="0.1"
                        />
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-warning/80 mt-3">
                    <Info className="w-3 h-3 inline mr-1" />
                    Maksymalna waga dla kuriera InPost: 30 kg
                  </p>
                </div>
              </div>

              {/* Insurance Section */}
              <div className="p-4 bg-surface-hover/20 rounded-xl border border-border-subtle">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-text-primary font-medium flex items-center">
                    <Shield className="w-4 h-4 mr-2 text-success" />
                    Ubezpieczenie przesyłek
                  </h4>
                  <div
                    onClick={() => setInsuranceEnabled(!insuranceEnabled)}
                    className={`relative w-12 h-6 rounded-full cursor-pointer transition-colors ${
                      insuranceEnabled ? 'bg-success' : 'bg-surface-hover'
                    }`}
                  >
                    <div
                      className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                        insuranceEnabled ? 'left-7' : 'left-1'
                      }`}
                    />
                  </div>
                </div>

                {insuranceEnabled && (
                  <div className="space-y-3">
                    <label
                      className={`flex items-start p-3 rounded-lg cursor-pointer transition-all ${
                        insuranceType === 'order_value'
                          ? 'bg-success/10 border border-success/50'
                          : 'bg-surface-hover/30 border border-transparent hover:bg-surface-hover/50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="insuranceType"
                        value="order_value"
                        checked={insuranceType === 'order_value'}
                        onChange={(e) => setInsuranceType(e.target.value as InsuranceType)}
                        className="sr-only"
                      />
                      <div className={`w-4 h-4 rounded-full border-2 mr-3 mt-0.5 flex items-center justify-center ${
                        insuranceType === 'order_value'
                          ? 'border-success bg-success'
                          : 'border-gray-500'
                      }`}>
                        {insuranceType === 'order_value' && (
                          <div className="w-1.5 h-1.5 rounded-full bg-surface-base" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-text-primary">Wartość zamówienia</p>
                        <p className="text-xs text-text-muted">Ubezpieczenie na pełną wartość zamówienia</p>
                      </div>
                    </label>

                    <label
                      className={`flex items-start p-3 rounded-lg cursor-pointer transition-all ${
                        insuranceType === 'cod_value'
                          ? 'bg-success/10 border border-success/50'
                          : 'bg-surface-hover/30 border border-transparent hover:bg-surface-hover/50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="insuranceType"
                        value="cod_value"
                        checked={insuranceType === 'cod_value'}
                        onChange={(e) => setInsuranceType(e.target.value as InsuranceType)}
                        className="sr-only"
                      />
                      <div className={`w-4 h-4 rounded-full border-2 mr-3 mt-0.5 flex items-center justify-center ${
                        insuranceType === 'cod_value'
                          ? 'border-success bg-success'
                          : 'border-gray-500'
                      }`}>
                        {insuranceType === 'cod_value' && (
                          <div className="w-1.5 h-1.5 rounded-full bg-surface-base" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-text-primary">Tylko COD (pobranie)</p>
                        <p className="text-xs text-text-muted">Ubezpieczenie tylko dla przesyłek pobraniowych</p>
                      </div>
                    </label>

                    <label
                      className={`flex items-start p-3 rounded-lg cursor-pointer transition-all ${
                        insuranceType === 'fixed'
                          ? 'bg-success/10 border border-success/50'
                          : 'bg-surface-hover/30 border border-transparent hover:bg-surface-hover/50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="insuranceType"
                        value="fixed"
                        checked={insuranceType === 'fixed'}
                        onChange={(e) => setInsuranceType(e.target.value as InsuranceType)}
                        className="sr-only"
                      />
                      <div className={`w-4 h-4 rounded-full border-2 mr-3 mt-0.5 flex items-center justify-center ${
                        insuranceType === 'fixed'
                          ? 'border-success bg-success'
                          : 'border-gray-500'
                      }`}>
                        {insuranceType === 'fixed' && (
                          <div className="w-1.5 h-1.5 rounded-full bg-surface-base" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-text-primary">Stała kwota</p>
                        <p className="text-xs text-text-muted">Ta sama kwota ubezpieczenia dla wszystkich</p>
                      </div>
                    </label>

                    {insuranceType === 'fixed' && (
                      <div className="mt-3 ml-7 p-3 bg-surface-raised/50 rounded-lg">
                        <label className="text-xs text-text-secondary mb-1 block">Kwota ubezpieczenia (PLN)</label>
                        <input
                          type="number"
                          value={fixedInsuranceAmount || ''}
                          onChange={(e) => setFixedInsuranceAmount(parseFloat(e.target.value) || 0)}
                          placeholder="500"
                          className="input text-sm w-32"
                          min="1"
                          max="50000"
                        />
                      </div>
                    )}

                    <div className="p-3 bg-success/10 border border-success/30 rounded-lg mt-4">
                      <p className="text-xs text-text-secondary">
                        <Info className="w-3 h-3 inline mr-1 text-success" />
                        InPost automatycznie dolicza ubezpieczenie do ceny przesyłki.
                        Dla przesyłek COD kurierem ubezpieczenie jest wymagane i musi być ≥ wartości pobrania.
                      </p>
                    </div>
                  </div>
                )}

                {!insuranceEnabled && (
                  <p className="text-sm text-text-muted">
                    Włącz, aby automatycznie dodawać ubezpieczenie do przesyłek.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Senders Tab */}
          {activeTab === 'senders' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-text-secondary">
                  Skonfiguruj nadawców, których dane będą używane na etykietach.
                </p>
                <button
                  onClick={() => setShowAddSenderModal(true)}
                  className="btn-secondary text-sm"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Dodaj nadawcę
                </button>
              </div>

              {senders.length === 0 ? (
                <div className="bg-surface-hover/20 rounded-xl">
                  <EmptyState
                    icon={User}
                    title="Brak skonfigurowanych nadawców"
                    description="Możesz powiązać nadawcę z konkretnym sklepem."
                    buttonLabel="Dodaj pierwszego nadawcę"
                    onButtonClick={() => setShowAddSenderModal(true)}
                    buttonIcon={Plus}
                    size="sm"
                  />
                </div>
              ) : (
                <div className="space-y-3">
                  {senders.map((sender) => {
                    const linkedStore = stores.find(s => s.id === sender.storeId);
                    return (
                      <div
                        key={sender.id}
                        className={`p-4 rounded-xl border ${
                          sender.isDefault
                            ? 'bg-accent-primary/5 border-accent-primary/30'
                            : 'bg-surface-hover/30 border-transparent'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className="text-text-primary font-medium">{sender.companyName || sender.name}</h4>
                              {sender.isDefault && (
                                <span className="badge badge-success text-xs">Domyślny</span>
                              )}
                            </div>
                            <p className="text-sm text-text-secondary mt-1">
                              {sender.street} {sender.buildingNumber}, {sender.postCode} {sender.city}
                            </p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-text-muted">
                              <span><Phone className="w-3 h-3 inline mr-1" />{sender.phone}</span>
                              <span><Mail className="w-3 h-3 inline mr-1" />{sender.email}</span>
                            </div>
                            {linkedStore && (
                              <div className="mt-2">
                                <span className="inline-flex items-center px-2 py-1 bg-accent-secondary/20 text-accent-secondary text-xs rounded-lg">
                                  <Store className="w-3 h-3 mr-1" />
                                  {linkedStore.name}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {!sender.isDefault && (
                              <button
                                onClick={() => handleSetDefaultSender(sender.id)}
                                className="p-2 rounded-lg text-text-secondary hover:text-accent-primary hover:bg-surface-hover transition-all"
                                title="Ustaw jako domyślny"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => setEditingSender(sender)}
                              className="p-2 rounded-lg text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-all"
                              title="Edytuj"
                            >
                              <Settings className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteSender(sender.id)}
                              className="p-2 rounded-lg text-text-secondary hover:text-danger hover:bg-danger/10 transition-all"
                              title="Usuń"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-4 p-6 border-t border-border-subtle">
          <div>
            {saveResult && (
              <div className={`flex items-center gap-2 text-sm ${
                saveResult.success ? 'text-success' : 'text-danger'
              }`}>
                {saveResult.success ? (
                  <CheckCircle className="w-4 h-4" />
                ) : (
                  <XCircle className="w-4 h-4" />
                )}
                {saveResult.message}
              </div>
            )}
          </div>
          <div className="flex justify-between gap-3">
            <button
              onClick={handleSave}
              disabled={saving || (!token && !config.configured) || !organizationId}
              className="btn-primary"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Zapisz konfigurację
            </button>
            <button type="button" onClick={onClose} className="btn-secondary">
              Anuluj
            </button>
          </div>
        </div>
      </div>

      {/* Add/Edit Sender Modal */}
      {(showAddSenderModal || editingSender) && (
        <SenderModal
          sender={editingSender}
          stores={stores}
          onClose={() => {
            setShowAddSenderModal(false);
            setEditingSender(null);
          }}
          onSave={(sender) => {
            if (editingSender) {
              handleEditSender({ ...sender, id: editingSender.id });
            } else {
              handleAddSender(sender);
            }
          }}
        />
      )}
    </div>
  );
}

// Sender Modal Component
function SenderModal({
  sender,
  stores,
  onClose,
  onSave,
}: {
  sender: Sender | null;
  stores: StoreType[];
  onClose: () => void;
  onSave: (sender: Omit<Sender, 'id'>) => void;
}) {
  const [formData, setFormData] = useState({
    name: sender?.name || '',
    companyName: sender?.companyName || '',
    phone: sender?.phone || '',
    email: sender?.email || '',
    street: sender?.street || '',
    buildingNumber: sender?.buildingNumber || '',
    city: sender?.city || '',
    postCode: sender?.postCode || '',
    storeId: sender?.storeId || '',
    isDefault: sender?.isDefault || false,
  });

  // Handle ESC key to close modal
  useEscapeKey(onClose);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...formData,
      storeId: formData.storeId || null,
    });
  };

  return (
    <div className="fixed inset-0 bg-surface-base/80 backdrop-blur-sm flex items-center justify-center p-4 z-[60]">
      <div className="bg-surface-raised border border-border rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border-subtle">
          <h2 className="text-xl font-semibold text-text-primary">
            {sender ? 'Edytuj nadawcę' : 'Dodaj nadawcę'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">
                <User className="w-4 h-4 inline mr-2" />
                Imię i nazwisko
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input"
                placeholder="Jan Kowalski"
              />
            </div>
            <div>
              <label className="label">
                <Building className="w-4 h-4 inline mr-2" />
                Nazwa firmy
              </label>
              <input
                type="text"
                value={formData.companyName}
                onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                className="input"
                placeholder="Moja Firma Sp. z o.o."
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">
                <Phone className="w-4 h-4 inline mr-2" />
                Telefon *
              </label>
              <input
                type="text"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="input"
                placeholder="+48123456789"
              />
            </div>
            <div>
              <label className="label">
                <Mail className="w-4 h-4 inline mr-2" />
                Email *
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="input"
                placeholder="kontakt@firma.pl"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <label className="label">
                <Map className="w-4 h-4 inline mr-2" />
                Ulica *
              </label>
              <input
                type="text"
                required
                value={formData.street}
                onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                className="input"
                placeholder="ul. Krakowska"
              />
            </div>
            <div>
              <label className="label">Nr budynku *</label>
              <input
                type="text"
                required
                value={formData.buildingNumber}
                onChange={(e) => setFormData({ ...formData, buildingNumber: e.target.value })}
                className="input"
                placeholder="15"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">
                <MapPin className="w-4 h-4 inline mr-2" />
                Miasto *
              </label>
              <input
                type="text"
                required
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="input"
                placeholder="Warszawa"
              />
            </div>
            <div>
              <label className="label">Kod pocztowy *</label>
              <input
                type="text"
                required
                value={formData.postCode}
                onChange={(e) => setFormData({ ...formData, postCode: e.target.value })}
                className="input"
                placeholder="00-001"
              />
            </div>
          </div>

          <div>
            <label className="label">
              <Store className="w-4 h-4 inline mr-2" />
              Powiązany sklep
            </label>
            <select
              value={formData.storeId}
              onChange={(e) => setFormData({ ...formData, storeId: e.target.value })}
              className="input"
            >
              <option value="">Brak (używaj dla wszystkich sklepów)</option>
              {stores.map((store) => (
                <option key={store.id} value={store.id}>
                  {store.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-text-muted mt-1">
              Powiąż nadawcę z konkretnym sklepem - będzie używany dla zamówień z tego sklepu
            </p>
          </div>

          <div className="flex items-center gap-3 p-4 bg-surface-hover/30 rounded-xl">
            <input
              type="checkbox"
              id="isDefault"
              checked={formData.isDefault}
              onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
              className="w-4 h-4 rounded border-border bg-surface-raised text-accent-primary focus:ring-accent-primary"
            />
            <label htmlFor="isDefault" className="text-sm text-text-secondary cursor-pointer">
              Ustaw jako domyślnego nadawcę
            </label>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="submit" className="btn-primary flex-1">
              <CheckCircle className="w-4 h-4 mr-2" />
              {sender ? 'Zapisz zmiany' : 'Dodaj nadawcę'}
            </button>
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              Anuluj
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
